import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import {
  ApiPathEnum,
  ChargesWithdrawalsEnum,
} from '../constants/endpoints.enum';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { StorageService } from './storage.service';
import { StreamEvent } from '../interfaces/ui/external-import.interface';
import { ApiResponse } from '../interfaces/api-response.inteface';
import {
  ChargesWithdrawalsCategory,
  ChargesWithdrawalsFilters,
  ChargesWithdrawalsListData,
  ChargesWithdrawalsResponse,
  ChargesWithdrawalsSelectableUsersData,
} from '../interfaces/charges-withdrawals.interface';
import {
  USER_SESSION,
  USER_SESSION_PRE,
} from '../../shared/data/balance/contants';

@Injectable({
  providedIn: 'root',
})
export class ChargesWithdrawalsService {
  public API_URL = `${environment.API_URL}${ApiPathEnum.ChargesWithdrawals}`;
  public API_URL_CATEGORY = `${environment.API_URL}${ApiPathEnum.Category}`;

  constructor(
    private http: HttpClient,
    private storageService: StorageService,
  ) {}

  private getToken(): string {
    try {
      const raw =
        this.storageService.secureStorage.getItem(USER_SESSION) ??
        this.storageService.secureStorage.getItem(USER_SESSION_PRE);
      return JSON.parse(raw)?.data?.token ?? '';
    } catch {
      return '';
    }
  }

  getAllLw(
    page: number,
    limit: number,
    filters: ChargesWithdrawalsFilters = {},
  ): Observable<ApiResponse<ChargesWithdrawalsListData>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());
    Object.entries(filters ?? {}).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        params = params.set(key, String(value));
      }
    });

    return this.http.get<ApiResponse<ChargesWithdrawalsListData>>(
      `${this.API_URL}${ChargesWithdrawalsEnum.GetAllLw}`,
      { params }
    );
  }
  getUserEditLw(
    page: number,
    limit: number,
    searchTerm?: string
  ): Observable<ChargesWithdrawalsResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());
    if (searchTerm) {
      params = params.set('searchTerm', searchTerm);
    }
    return this.http.get<ChargesWithdrawalsResponse>(
      `${this.API_URL}${ChargesWithdrawalsEnum.GetUserEdiLw}`,
      { params }
    );
  } 
  downloadLwData(): Observable<ApiResponse<unknown>> {
    return this.http.get<ApiResponse<unknown>>(
      `${this.API_URL}${ChargesWithdrawalsEnum.DownloadLwData}`
    );
  }
  setState(
    idcargas_retiros: number,
    id_estado: number,
    comentario: string
  ): Observable<ApiResponse<null | undefined>> {
    const body = {
      idcargas_retiros: idcargas_retiros,
      id_estado: id_estado,
      comentario,
    };
    return this.http.patch<ApiResponse<null | undefined>>(
      `${this.API_URL}${ChargesWithdrawalsEnum.SetState}`,
      body
    );
  }

  getCategories(): Observable<ApiResponse<ChargesWithdrawalsCategory[]>> {
    return this.http.get<ApiResponse<ChargesWithdrawalsCategory[]>>(
      `${this.API_URL_CATEGORY}${ChargesWithdrawalsEnum.GetCategories}`
    );
  }
  getUsersForSelect(
    page: number,
    limit: number,
    username?: string,
    type?: string
  ): Observable<ApiResponse<ChargesWithdrawalsSelectableUsersData>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    if (username) {
      params = params.set('username', username);
    }
    if (type) {
      params = params.set('type', type);
    }

    return this.http.get<ApiResponse<ChargesWithdrawalsSelectableUsersData>>(
      `${this.API_URL}${ChargesWithdrawalsEnum.GetUsersForSelect}`,
      { params }
    );
  }

  insertExternalData(
    fromDate?: string,
    toDate?: string,
    page: number = 1
  ): Observable<ApiResponse<{ inserted: number; skipped: number; page: number; totalPages: number; total: number; noData: boolean; dateRange: { from: string; to: string } | null }>> {
    let params = new HttpParams().set('isInsert', 'true').set('page', String(page));
    if (fromDate) params = params.set('fromDate', fromDate);
    if (toDate) params = params.set('toDate', toDate);

    return this.http.get<ApiResponse<{ inserted: number; skipped: number; page: number; totalPages: number; total: number; noData: boolean; dateRange: { from: string; to: string } | null }>>(
      `${this.API_URL}${ChargesWithdrawalsEnum.GetAllExternalData}`,
      { params }
    );
  }

  streamExternalData(fromDate?: string, toDate?: string): Observable<StreamEvent> {
    let params = new HttpParams().set('isInsert', 'true');
    if (fromDate) params = params.set('fromDate', fromDate);
    if (toDate) params = params.set('toDate', toDate);
    const url = `${this.API_URL}${ChargesWithdrawalsEnum.GetAllExternalData}?${params.toString()}`;
    const token = this.getToken();

    return new Observable<StreamEvent>((observer) => {
      let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
      let cancelled = false;

      fetch(url, {
        headers: {
          'X-API-KEY': token,
          Accept: 'text/event-stream',
        },
      })
        .then((response) => {
          if (!response.ok) {
            observer.error(new Error(`HTTP ${response.status}`));
            return;
          }

          reader = response.body!.getReader();
          const decoder = new TextDecoder();
          let buffer = '';

          const pump = async (): Promise<void> => {
            while (!cancelled) {
              const { done, value } = await reader!.read();
              if (done) { observer.complete(); return; }

              buffer += decoder.decode(value, { stream: true });
              const parts = buffer.split('\n\n');
              buffer = parts.pop() ?? '';

              for (const part of parts) {
                const dataLine = part.split('\n').find((l) => l.startsWith('data: '));
                if (dataLine) {
                  try { observer.next(JSON.parse(dataLine.slice(6))); } catch { }
                }
              }
            }
          };

          pump().catch((err) => observer.error(err));
        })
        .catch((err) => observer.error(err));

      return () => {
        cancelled = true;
        reader?.cancel();
      };
    });
  }
}
